/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useEffect, useState, useMemo } from 'react'
// utils
import { isHexColorMuted, isLight } from 'utils/theme'
// material
import { ColorPicker, SingleSelectDropDown, Switch, ToolTip, Tip } from '@insticator/insticator-ui/dist/material'
import Space from 'material/space/space';
import { SectionGroupV2 } from 'material/expandablesection/v2'
import ColorPalette from './colorpalette/colorpalette'
import { ReactComponent as LightMode } from 'assets/icons/light-mode.svg'
import { ReactComponent as DarkMode } from 'assets/icons/dark-mode.svg'
// styles
import injectSheet from 'react-jss'
import styles from './colors_styles'

const extensionOptions = [
  { name: 'Default', content: <ColorPalette name={'DEFAULT'} />, value: 'DEFAULT'},
  { name: 'Sunny', content: <ColorPalette name={'SUNNY'} />, value: 'SUNNY'},
  { name: 'Muted', content: <ColorPalette name={'MUTED'} />, value: 'MUTED'},
  { name: 'Chic', content: <ColorPalette name={'CHIC'} />, value: 'CHIC'},
  { name: 'Smart', content: <ColorPalette name={'SMART'} />, value: 'SMART'},
  { name: 'Sporty', content: <ColorPalette name={'SPORTY'} />, value: 'SPORTY'},
  { name: 'Verdant', content: <ColorPalette name={'VERDANT'} />, value: 'VERDANT'},
  { name: 'Trendsetting', content: <ColorPalette name={'TRENDSETTING'} />, value: 'TRENDSETTING'},
  { name: 'Grayscale', content: <ColorPalette name={'GRAYSCALE'} />, value: 'GRAYSCALE'}
]

const extensionMap = (name) => {
  return extensionOptions.filter(extension => extension.value === name)?.pop() || extensionOptions[0]
}
/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2: SITE THEME SECTION : COLORS ~~~~~~~~~~ ========== */
const Colors = injectSheet(styles)((props) => {

  const { siteTheme, setSiteThemeConfig, setSiteThemeConfigs, isConfigOpen } = props

  const [colorSchema, ] = useState({
    customBackgroundColor: siteTheme.customBackgroundColor,
    customPrimaryColor: siteTheme.customPrimaryColor
  })

  const [tempColors, setTempColors] = useState({
    customPrimaryColor: siteTheme.customPrimaryColor
  })

  const [colorSchemaValid, setColorSchemaValid] = useState({
      customBackgroundColor: true, customPrimaryColor: true
  })


  useEffect(() => {
    if(!isConfigOpen) {
      setColorSchemaValid({customBackgroundColor: true, customPrimaryColor: true})
      setTempColors({ customPrimaryColor: siteTheme.customPrimaryColor})
    } 
  },[isConfigOpen, siteTheme.customPrimaryColor])


  const [extension, setExtension] = useState({
    ...extensionMap(siteTheme.themeExtension), 
    name: <ColorPalette text={extensionMap(siteTheme.themeExtension).name} name={extensionMap(siteTheme.themeExtension).value} />
  })

  useEffect(() => {
    // if there is a change on extension option reflect in the dropdown
    setExtension({
      ...extensionMap(siteTheme.themeExtension), 
      name: <ColorPalette text={extensionMap(siteTheme.themeExtension).name} name={extensionMap(siteTheme.themeExtension).value} />
    })
  }, [siteTheme.themeExtension])

  const handleSelectThemeExtension = (opt) => {
    setExtension({...opt, name: <ColorPalette text={opt.name} name={opt.value} />})
    setSiteThemeConfig('themeExtension', opt.value)
  }

  const handlePrimaryColorChange = color => {
    setTempColors({ customPrimaryColor: color })
    setSiteThemeConfig('customPrimaryColor', color)
  }

  const handleBackgroundChange = (color) => {
    const modeStyle = isLight(color) ? 'LIGHT' : 'DARK'
    setSiteThemeConfigs({
      customBackgroundColor: color
    })
  }

  const toggleThemeMode = () => {
    const modeStyle = siteTheme.commentingModuleStyle === 'LIGHT' ? 'DARK' : 'LIGHT'
    handleModeChange(modeStyle)
  }

  const handleModeChange = (modeStyle) => {
    setSiteThemeConfig('commentingModuleStyle', modeStyle)
  }

  const validateColor = (isValid, name) => {
    setColorSchemaValid({
      ...colorSchemaValid,
      [name]: isValid
    })
  }

  const handleReset = (color) => setSiteThemeConfig(color, colorSchema[color])

  const handleMonochromeChange = () => {
    setSiteThemeConfigs({
      enableMonochrome: !siteTheme.enableMonochrome,
      customPrimaryColor: 
        siteTheme.enableMonochrome ? (tempColors.customPrimaryColor === '#000000' ? '#061240' : tempColors.customPrimaryColor) 
        : tempColors.customPrimaryColor
    })
  }

  const hasLowSaturation = useMemo(() => {
    if(siteTheme.enableMonochrome) return false
    return isHexColorMuted(siteTheme.customPrimaryColor)
  }, [siteTheme.customPrimaryColor, siteTheme.enableMonochrome])
      
      

  return (
    <SectionGroupV2 
      klass={`colors ${props.classes.colors}`}
      title={'Colors'} 
      description={'Match your sites color scheme by selecting some basic hues.'}
      direction={'column'}
    >
      <Space klass={'backgroundcolor'} alignItems={'center'}>
        <ColorPicker 
          valid={colorSchemaValid.customBackgroundColor}
          color={siteTheme.customBackgroundColor || '#FFFFFF'}
          savedColor={siteTheme.customBackgroundColor || '#FFFFFF'}
          label={<span>Background <Tip space={'none'} icon={'question-circle'} content={'This color should match the background color where your comments unit will display.'} label={''} /></span>}
          handleColorChange={(color, e) => handleBackgroundChange(color)}
          processColorUpdate={(color, e) => handleBackgroundChange(color)}
          validateColor={(isValid, e) => validateColor(isValid, 'customBackgroundColor')}
          handleReset={() => handleReset('customBackgroundColor')} 
        />
        {/* <Space klass={'mode'} alignItems={'flex-end'}>
          <ToolTip position={`top`} action={`mouseenter`} content={'Light Mode'}>
            <LightMode fill={siteTheme.commentingModuleStyle === 'LIGHT' ? '#F9DA56' : '#DFDFDF'} />
          </ToolTip>
          <Switch on={siteTheme.commentingModuleStyle === 'DARK'} handleClick={toggleThemeMode} />
          <ToolTip position={`top`} action={`mouseenter`} content={'Dark Mode'}>
            <DarkMode fill={siteTheme.commentingModuleStyle === 'DARK' ? '#061240' : '#DFDFDF'} />
          </ToolTip>
        </Space> */}
      </Space>

    <Space klass={`primarycolor disabled-${!!siteTheme.enableMonochrome}`} alignItems={'center'}>
      <ColorPicker 
        valid={colorSchemaValid.customPrimaryColor} 
        label={'Primary'} 
        color={siteTheme.customPrimaryColor || '#061240'} 
        savedColor={siteTheme.customPrimaryColor || '061240'} 
        processColorUpdate={(color, e) => {
          handlePrimaryColorChange(color)
        }}
        handleColorChange={(color, e) => handlePrimaryColorChange(color)}
        validateColor={(isValid, e) => validateColor(isValid, 'customPrimaryColor')} 
        handleReset={() => handleReset('customPrimaryColor')} 
      />
        <Space klass={'usemonochrome'} gap={8} alignItems={'center'}>
          <Switch 
            on={!!siteTheme.enableMonochrome} 
            handleClick={() => handleMonochromeChange(!siteTheme.enableMonochrome)} 
          />
          <span>Monochrome</span>
          <Tip 
            content={`When enabled, the commenting unit will display in grayscale.`}
            label={''}
          />
        </Space>
    </Space>
    <div className='errormessage'>{hasLowSaturation && `Color has low saturation. Please select another primary color.`}</div>

      <SingleSelectDropDown 
        name={'extensiondropdown'}
        label={<span>Extension <Tip space={'none'} icon={'question-circle'} content={'This will apply a mixture of colors to elements within the comments unit like avatars, user status badges, and more.'} label={''} /></span>}
        selected={extension}
        searchable={false} 
        onSelect={handleSelectThemeExtension}
        items={extensionOptions}
      />
    </SectionGroupV2>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default Colors